import React from 'react';
import { Grid } from '@material-ui/core';
import {
  Page,
  Header,
  StatusOK,
  StatusWarning,
  ResponseErrorPanel,
  Progress,
  InfoCard,
  Table,
} from '@backstage/core-components';
import useAsync from 'react-use/lib/useAsync';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { Sparklines, SparklinesLine } from 'react-sparklines';

type Summary = {
  stats: PrometheusStat[];
  alerts: Alert[];
};

type Alert = {
  alert: string;
  namespace: string;
};

type PrometheusQuery = {
  service: string;
  displayName: string;
};

type PrometheusStat = {
  serviceName: string;
  value: string;
  values: number[];
};

type PrometheusResult = {
  status: string;
  data: {
    resultType: string;
    result: {
      metric: any;
      value: any[];
    }[];
  };
};

type PrometheusResults = {
  status: string;
  data: {
    resultType: string;
    result: {
      metric: any;
      values: any[][];
    }[];
  };
};

const stats_columns = [
  { field: 'status', title: 'Status' },
  { field: 'serviceName', title: 'Service' },
  { field: 'value', title: 'Operations Per Second' },
  { field: 'trend', title: 'Traffic' },
];

const alerts_columns = [
  { field: 'status', title: 'Status' },
  { field: 'alert', title: 'Alert Name' },
  { field: 'namespace', title: 'Namespace' },
];

const containerStyle = { padding: 20 };

export const HomePage = () => {
  const discoveryApi = useApi(discoveryApiRef);
  const { fetch } = useApi(fetchApiRef);

  const { value, loading, error } = useAsync(async (): Promise<Summary> => {
    const stats = [] as PrometheusStat[];
    const date = new Date();
    const formatted_time = date.toISOString();
    const base_url = await discoveryApi.getBaseUrl('proxy');

    const services = [
      { service: 'blackpearl', displayName: 'Authentication' },
      { service: 'rockshrimp', displayName: 'Configuration API' },
      { service: 'pelican-graphql', displayName: 'AI Discovery API' },
      { service: 'oyster-graphql', displayName: 'Jobs API' },
    ] as PrometheusQuery[];

    for (const service of services) {
      let response = await fetch(
        `${base_url}/prometheus/api/query?query= round(sum(rate(activator_request_count{namespace_name="marketplace", configuration_name=~"${service.service}"}[1m])) by (revision_name), 0.001) &time=${formatted_time}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );
      const api_response = (await response.json()) as PrometheusResult;
      let metric_value = 'No Traffic';
      for (const metric of api_response.data.result) {
        metric_value = metric.value[1];
      }

      date.setHours(date.getHours() - 1);
      const start = date.toISOString();
      response = await fetch(
        `${base_url}/prometheus/api/query_range?query=round(sum(rate(activator_request_count{namespace_name="marketplace", configuration_name=~"${service.service}"}[1m])) by (revision_name), 0.001)&start=${start}&end=${formatted_time}&step=60`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );
      const api_response_metrics = (await response.json()) as PrometheusResults;
      const values = [];
      for (const metric of api_response_metrics.data.result) {
        for (const metric_number of metric.values) {
          values.push(Number(metric_number[1]));
        }
      }

      stats.push({
        serviceName: service.displayName,
        value: metric_value,
        values: values,
      });
    }

    const response = await fetch(
      `${base_url}/prometheus/api/query?query=ALERTS{alertstate="firing"}&time=${formatted_time}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );
    const api_response = (await response.json()) as PrometheusResult;
    const alerts = [] as Alert[];
    for (const metric of api_response.data.result) {
      alerts.push({
        alert: metric.metric.alertname,
        namespace: metric.metric.namespace,
      });
    }

    return { stats, alerts };
  }, []);

  if (loading) {
    return <Progress />;
  } else if (error) {
    return <ResponseErrorPanel error={error} />;
  }

  if (value != null) {
    const stats = value.stats.map(v => {
      return {
        serviceName: v.serviceName,
        value: v.value,
        status: <StatusOK>OK</StatusOK>,
        trend: (
          <Sparklines data={v.values} margin={5}>
            <SparklinesLine color="green" />
          </Sparklines>
        ),
      };
    });

    let alerts = <div />;
    if (value.alerts.length > 0) {
      const alert_names = value.alerts.map(a => {
        return {
          status: <StatusWarning>Firing</StatusWarning>,
          alert: a.alert,
          namespace: a.namespace,
        };
      });

      alerts = (
        <InfoCard title="Alerts" noPadding>
          <Table
            options={{
              search: false,
              paging: false,
              toolbar: false,
            }}
            data={alert_names}
            columns={alerts_columns}
          />
        </InfoCard>
      );
    }

    return (
      <Page themeId="tool">
        <Header title="Welcome to deepcOS Cloud administration" />
        <Grid
          container
          justifyContent="center"
          direction="row"
          spacing={6}
          style={containerStyle}
        >
          <Grid item xs={12}>
            <InfoCard title="Service Health" noPadding>
              <Table
                options={{
                  search: false,
                  paging: false,
                  toolbar: false,
                }}
                data={stats}
                columns={stats_columns}
              />
            </InfoCard>
          </Grid>
          <Grid item xs={12}>
            {alerts}
          </Grid>
        </Grid>
      </Page>
    );
  }

  return <Progress />;
};
